// contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { apiService, apiEvents } from '../services/api'; // 👈 import apiEvents
import { User, LoginRequest, AuthContextType } from '../types/index';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  const _clearSession = useCallback((redirectToLogin = false, expired = false) => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    apiService.clearAuthToken();
    setUser(null);
    setIsAuthenticated(false);

    if (redirectToLogin) {
      navigate(expired ? '/login?sessionExpired=true' : '/login', { replace: true });
    }
  }, [navigate]);

  // 👇 Listen for 401s emitted by the API interceptor mid-session
  useEffect(() => {
    const handleUnauthorized = () => {
      // Only redirect if user was actually logged in — avoids redirect on the login page itself
      const hasToken = !!localStorage.getItem('token');
      if (hasToken) {
        _clearSession(true, true);
      }
    };

    apiEvents.on('auth:unauthorized', handleUnauthorized);
    return () => {
      apiEvents.off('auth:unauthorized', handleUnauthorized);
    };
  }, [_clearSession]);

  // Restore session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          apiService.setAuthToken(token);
          const parsed: User = JSON.parse(storedUser);
          setUser(parsed);
          setIsAuthenticated(true);

          try {
            const profile = await authService.getProfile();
            setUser(profile);
            localStorage.setItem('user', JSON.stringify(profile));
          } catch {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              try {
                const refreshed = await authService.refreshToken(refreshToken);
                localStorage.setItem('token', refreshed.access);
                apiService.setAuthToken(refreshed.access);
                if (refreshed.refresh) {
                  localStorage.setItem('refresh_token', refreshed.refresh);
                }
                const profile = await authService.getProfile();
                setUser(profile);
                localStorage.setItem('user', JSON.stringify(profile));
              } catch {
                // Both tokens dead on startup — redirect with expired flag
                _clearSession(true, true);
              }
            } else {
              _clearSession(true, true);
            }
          }
        } catch {
          _clearSession(true, true);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [_clearSession]);

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    try {
      const response = await authService.login(credentials);
      localStorage.setItem('token', response.tokens.access);
      localStorage.setItem('refresh_token', response.tokens.refresh);
      localStorage.setItem('user', JSON.stringify(response.user));
      apiService.setAuthToken(response.tokens.access);
      setUser(response.user);
      setIsAuthenticated(true);
      return true;
    } catch {
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
    } catch {
      // Swallow — always clear session
    } finally {
      _clearSession(true, false); // intentional logout — no "expired" flag
    }
  }, [_clearSession]);

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    await authService.forgotPassword(email);
  }, []);

  const resetPassword = useCallback(async (payload: {
    email: string;
    otp_code: string;
    password: string;
    password_confirm: string;
  }): Promise<void> => {
    await authService.resetPassword({
      token: payload.otp_code,
      new_password: payload.password,
      confirm_password: payload.password_confirm,
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated, login, logout, requestPasswordReset, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};